// Wspólny wybór nagłówka (region, węzły, okres) — zapamiętany dla użytkownika i przenoszony między modułami.
// Węzeł to para (region, numer węzła): numer jest unikalny tylko w obrębie regionu.

import { GOTOWE_OKRESY, poprawnaData, type GotowyOkres, type Okres } from './okres'

export type WezelKlucz = { regionId: number; wezelId: number }

export type WspolnyWybor = {
  /** null = wszystkie regiony. */
  regionId: number | null
  /** Pusta lista = wszystkie węzły. */
  wezly: WezelKlucz[]
  okres: Okres
}

export function domyslnyWybor(): WspolnyWybor {
  return { regionId: null, wezly: [], okres: { gotowy: 'miesiac' } }
}

const dodatnia = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v > 0

function normalizujOkres(v: unknown): Okres {
  const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>
  if (typeof o.gotowy === 'string' && (GOTOWE_OKRESY as string[]).includes(o.gotowy)) {
    return { gotowy: o.gotowy as GotowyOkres }
  }
  if (poprawnaData(o.od) && poprawnaData(o.do) && o.od <= o.do) return { od: o.od, do: o.do }
  return domyslnyWybor().okres
}

/** Wartość z serwera (dowolna, także uszkodzona albo stara) → poprawny wybór, pole po polu. */
export function normalizujWybor(v: unknown): WspolnyWybor {
  if (!v || typeof v !== 'object') return domyslnyWybor()
  const o = v as Record<string, unknown>
  const wezly = Array.isArray(o.wezly)
    ? o.wezly.filter((w): w is WezelKlucz => !!w && typeof w === 'object'
        && dodatnia((w as WezelKlucz).regionId) && dodatnia((w as WezelKlucz).wezelId))
      .map((w) => ({ regionId: w.regionId, wezelId: w.wezelId }))
    : []
  return {
    regionId: dodatnia(o.regionId) ? o.regionId : null,
    wezly,
    okres: normalizujOkres(o.okres),
  }
}

/**
 * Region do pokazania w module: zapamiętany, gdy moduł go ma; inaczej „wszystkie” (gdy moduł je
 * dopuszcza) albo pierwszy dostępny. Zapamiętanego wyboru się przy tym nie nadpisuje.
 */
export function regionDlaModulu(zapamietany: number | null, dostepne: number[], pozwalaWszystkie: boolean): number | null {
  if (zapamietany != null && dostepne.includes(zapamietany)) return zapamietany
  if (pozwalaWszystkie) return null
  return dostepne.length ? dostepne[0] : null
}

export function wezlyRegionu(wezly: WezelKlucz[], regionId: number | null): WezelKlucz[] {
  return regionId == null ? wezly : wezly.filter((w) => w.regionId === regionId)
}

export function tenSamWezel(a: WezelKlucz, b: WezelKlucz): boolean {
  return a.regionId === b.regionId && a.wezelId === b.wezelId
}

/** Węzły do zapytań API: „region:węzeł” po przecinku. */
export function tekstWezlow(wezly: WezelKlucz[]): string {
  return wezly.map((w) => `${w.regionId}:${w.wezelId}`).join(',')
}
